import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../apiClient';
import { useApp } from '../appContext';

const PAGE_SIZE = 50;

// Same click-to-sort header as TimeReports.js/Configuration.js, duplicated rather than shared (the
// "no shared component layer" convention). Sorting here is server-side: the grid is paginated, so
// reordering only the visible page would misreport what's most recent.
function SortTh({ label, sortKey, sort, onSort, style }) {
  const active = sort.key === sortKey;
  return (
    <th
      style={{ textAlign: 'left', cursor: 'pointer', userSelect: 'none', ...style }}
      onClick={() => onSort(sortKey)}
    >
      {label} <span style={{ opacity: active ? 1 : 0.25 }}>{active && sort.dir === 'desc' ? '▼' : '▲'}</span>
    </th>
  );
}

// Checkbox dropdown (native <details>), duplicated from ResourcePlanning.js's team filter per the "no
// shared component layer" convention. `value` is an array of user ids; empty means everyone.
function PersonMultiSelect({ users, value, onChange }) {
  const label = value.length === 0
    ? 'Everyone'
    : value.length <= 2
      ? users.filter(u => value.includes(u.id)).map(u => u.name).join(', ')
      : `${value.length} people`;
  const toggle = id => onChange(value.includes(id) ? value.filter(v => v !== id) : [...value, id]);
  return (
    <details style={{ position: 'relative' }}>
      <summary className="form-select" style={{ cursor: 'pointer', listStyle: 'none' }}>{label}</summary>
      <div
        className="card"
        style={{ position: 'absolute', zIndex: 20, marginTop: 4, padding: 8, minWidth: 240, maxHeight: 300, overflowY: 'auto' }}
      >
        {users.map(u => (
          <label key={u.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '3px 2px', fontSize: 13, cursor: 'pointer' }}>
            <input type="checkbox" checked={value.includes(u.id)} onChange={() => toggle(u.id)} />
            {u.name}
          </label>
        ))}
      </div>
    </details>
  );
}

// Admin-only login history. One row per access token rather than per request -- see the UserSession
// comment in schema.prisma for why that's the unit, and what it can and can't tell you.
function LoginHistory() {
  const { currentUser } = useApp();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState({ key: 'startedAt', dir: 'desc' });
  const [users, setUsers] = useState([]);
  const [filters, setFilters] = useState({ userIds: [], from: '', to: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const isAdmin = !currentUser || currentUser.role === 'admin';

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        pageSize: String(PAGE_SIZE),
        sortBy: sort.key,
        sortDir: sort.dir,
      });
      if (filters.userIds.length) params.set('userId', filters.userIds.join(','));
      if (filters.from) params.set('from', filters.from);
      if (filters.to) params.set('to', filters.to);

      const data = await apiFetch(`/audit/sessions?${params.toString()}`);
      setRows(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [page, sort, filters]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  useEffect(() => {
    if (!isAdmin) return;
    // includeViewers: this is an audit screen, not a resourcing picker -- an EVP/Finance viewer's
    // sign-ins are exactly the kind of thing an admin might want to filter this list to.
    apiFetch('/users?includeViewers=true').then(setUsers).catch(() => setUsers([]));
  }, [isAdmin]);

  function handleSort(key) {
    setPage(1);
    setSort(prev => (prev.key === key ? { key, dir: prev.dir === 'desc' ? 'asc' : 'desc' } : { key, dir: 'desc' }));
  }

  function handleFilter(patch) {
    setPage(1);
    setFilters(prev => ({ ...prev, ...patch }));
  }

  // Defense in depth against a stray URL -- the API rejects non-admins regardless (@Roles('admin')
  // on the whole controller). Same pattern as Maintenance.js.
  if (currentUser && currentUser.role !== 'admin') {
    return (
      <div>
        <div className="page-header">
          <div className="page-eyebrow">Administration</div>
          <h1 className="page-title">Login History</h1>
        </div>
        <div className="card">
          <div style={{ fontSize: 13, color: 'var(--text-mid)' }}>
            This page is only available to administrators.
          </div>
        </div>
      </div>
    );
  }

  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="page-header">
        <div className="page-eyebrow">Administration</div>
        <h1 className="page-title">Login History</h1>
        <p className="page-subtitle">
          Who has been using the app, and when. One row per sign-in session.
        </p>
      </div>

      {error && (
        <div style={{
          padding: '12px 16px', borderRadius: 8, marginBottom: 20,
          background: 'rgba(239,65,54,0.08)', border: '1px solid rgba(239,65,54,0.3)',
          fontSize: 13, color: 'var(--red)',
        }}>⚠ {error}</div>
      )}

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ marginBottom: 0, minWidth: 220 }}>
            <label className="form-label">Person</label>
            <PersonMultiSelect users={users} value={filters.userIds} onChange={userIds => handleFilter({ userIds })} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-input"
              value={filters.from}
              onChange={e => handleFilter({ from: e.target.value })}
            />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-input"
              value={filters.to}
              onChange={e => handleFilter({ to: e.target.value })}
            />
          </div>
          {(filters.userIds.length > 0 || filters.from || filters.to) && (
            <button
              className="btn btn-outline"
              onClick={() => handleFilter({ userIds: [], from: '', to: '' })}
            >
              Clear filters
            </button>
          )}
          <div style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-mid)' }}>
            {loading ? 'Loading…' : `${total.toLocaleString()} session${total === 1 ? '' : 's'}`}
          </div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <table className="sheet">
          <thead>
            <tr>
              <SortTh label="Person" sortKey="user" sort={sort} onSort={handleSort} />
              <SortTh label="Signed in" sortKey="startedAt" sort={sort} onSort={handleSort} />
              <SortTh label="Last active" sortKey="lastSeenAt" sort={sort} onSort={handleSort} />
              <th style={{ textAlign: 'left' }}>Duration</th>
              <th style={{ textAlign: 'left' }}>IP address</th>
              <th style={{ textAlign: 'left' }}>Device</th>
            </tr>
          </thead>
          <tbody>
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={6} style={{ padding: 24, textAlign: 'center', fontSize: 13, color: 'var(--text-mid)' }}>
                  No sessions match these filters.
                </td>
              </tr>
            )}
            {rows.map(row => (
              <tr key={row.id}>
                <td>
                  <div style={{ fontWeight: 600 }}>{row.user?.name || '—'}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-mid)' }}>{row.user?.email}</div>
                </td>
                <td>{formatDateTime(row.startedAt)}</td>
                <td>{formatDateTime(row.lastSeenAt)}</td>
                <td>{formatDuration(row.startedAt, row.lastSeenAt)}</td>
                <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{row.ipAddress || '—'}</td>
                <td title={row.userAgent || ''}>{deviceLabel(row.userAgent)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {lastPage > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 16 }}>
            <button className="btn btn-outline" disabled={page <= 1 || loading} onClick={() => setPage(p => p - 1)}>
              ← Previous
            </button>
            <span style={{ fontSize: 12, color: 'var(--text-mid)' }}>
              Page {page} of {lastPage}
            </span>
            <button className="btn btn-outline" disabled={page >= lastPage || loading} onClick={() => setPage(p => p + 1)}>
              Next →
            </button>
          </div>
        )}
      </div>

      <div className="card">
        <div className="card-title">What this shows</div>
        <ul style={{ fontSize: 13, color: 'var(--text-mid)', lineHeight: 1.7, paddingLeft: 18, margin: 0 }}>
          <li>
            A row per sign-in session (about one an hour while someone is actively working), not one
            per click.
          </li>
          <li>
            "Last active" is accurate to within about five minutes — it isn't an exact sign-out time,
            and nobody is signed out when a session row stops updating.
          </li>
          <li>
            This is app usage, not Entra sign-ins. Someone who signs into Entra but never opens the
            Hub won't appear, and <strong>failed sign-in attempts are not recorded here</strong> —
            those live in Entra's own sign-in logs.
          </li>
          <li>History is kept for 13 months, then purged automatically.</li>
        </ul>
        <div style={{ marginTop: 16 }}>
          <Link to="/administration" className="btn btn-outline">← Back to Administration</Link>
        </div>
      </div>
    </div>
  );
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString() : '—';
}

function formatDuration(startedAt, lastSeenAt) {
  if (!startedAt || !lastSeenAt) return '—';
  const minutes = Math.round((new Date(lastSeenAt) - new Date(startedAt)) / 60000);
  if (minutes < 1) return '<1m';
  if (minutes < 60) return `${minutes}m`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}

// Deliberately crude: enough to tell a normal browser from something scripted hitting the API, which
// is the only question this column needs to answer. Full string is on the cell's tooltip.
function deviceLabel(userAgent) {
  if (!userAgent) return '—';
  const browser = /Edg\//.test(userAgent) ? 'Edge'
    : /Chrome\//.test(userAgent) ? 'Chrome'
    : /Safari\//.test(userAgent) ? 'Safari'
    : /Firefox\//.test(userAgent) ? 'Firefox'
    : null;
  if (!browser) return userAgent.slice(0, 40);
  const os = /Windows/.test(userAgent) ? 'Windows'
    : /Mac OS X|Macintosh/.test(userAgent) ? 'macOS'
    : /Android/.test(userAgent) ? 'Android'
    : /iPhone|iPad/.test(userAgent) ? 'iOS'
    : null;
  return os ? `${browser} · ${os}` : browser;
}

export default LoginHistory;
